import { useLocation, useNavigate } from 'react-router-dom';

export const FLEET_WEB_ROOT = '/fleet-inspections/web';
const sections = new Set(['dashboard', 'history', 'defects', 'driver', 'assets', 'truck', 'trailer', 'templates', 'equipment', 'members', 'settings', 'account']);
// Trucks, trailers and equipment administration now live on Asset Management; their old URLs redirect there.
export const LEGACY_ASSET_SECTIONS = ['truck', 'trailer', 'equipment'];
export const ASSET_TYPES = ['truck', 'trailer'];

// The Asset Management type filter (`type` query parameter); anything else means all equipment.
export function readAssetType(params) {
  const value = params.get('type');
  return ASSET_TYPES.includes(value) ? value : '';
}

export function readFleetRoute(pathname) {
  const parts = pathname.slice(FLEET_WEB_ROOT.length).split('/').filter(Boolean);
  const section = parts[0] || 'dashboard';
  try {
    const id = parts[1] ? decodeURIComponent(parts[1]) : '';
    const templateId = parts[2] ? decodeURIComponent(parts[2]) : '';
    const valid = sections.has(section) && (section === 'templates'
      ? parts.length <= 1 || (id === 'new' && parts.length === 2) ||
        (['organization', 'standard', 'suggestion'].includes(id) && Boolean(templateId) && parts.length === 3)
      : parts.length <= (['history', 'driver', 'assets', 'truck', 'trailer', 'equipment', 'settings'].includes(section) ? 2 : 1));
    return { section, id, templateId, valid };
  } catch (error) {
    return { section, id: '', templateId: '', valid: false };
  }
}

export function readPageCount(params, key = 'pages') {
  const value = params.get(key) ?? '1';
  if (!/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value))) {
    throw new Error('The requested pagination is invalid. Return to the list and try again.');
  }
  return Number(value);
}

// User-initiated navigations commit synchronously (like a plain router) so the page follows the click immediately;
// navigations issued from effects must pass { sync: false }, because React cannot flush synchronously there.
export function useFleetWebNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const go = (section, id = '', options = {}) => {
    const query = options.preserve ? new URLSearchParams(location.search) : new URLSearchParams();
    if (params.has('org')) query.set('org', params.get('org'));
    if (options.organization) query.set('org', options.organization);
    options.clear?.forEach((key) => query.delete(key));
    Object.entries(options.set || {}).forEach(([key, value]) => query.set(key, String(value)));
    const path = `${FLEET_WEB_ROOT}/${section}${id ? `/${id.split('/').map(encodeURIComponent).join('/')}` : ''}`;
    navigate({ pathname: path, search: query.toString() }, { replace: options.replace === true, flushSync: options.sync !== false });
  };
  const update = (values, { replace = false, keepEmpty = false, sync = true } = {}) => {
    const query = new URLSearchParams(location.search);
    Object.entries(values).forEach(([key, value]) => {
      if (value == null || (value === '' && !keepEmpty)) query.delete(key);
      else query.set(key, String(value));
    });
    navigate({ pathname: location.pathname, search: query.toString() }, { replace, flushSync: sync });
  };
  return { ...readFleetRoute(location.pathname), location, params, go, update };
}
